import { useEffect, useCallback } from 'react'
import { useEditContext } from '@/contexts/EditContext'
import { useContent } from './useContent'
import type { ContentData } from '@/types'

interface UseEditableSectionReturn {
  content: ContentData
  isLoading: boolean
  error: string | null
  isDirty: boolean
  updateField: (key: string, value: string) => void
  publish: () => Promise<void>
  discard: () => void
}

export function useEditableSection(sectionKey: string): UseEditableSectionReturn {
  const {
    content,
    isLoading,
    error,
    isDirty,
    updateField,
    publish: contentPublish,
    discardChanges,
  } = useContent(sectionKey)

  const {
    markDirty,
    markClean,
    registerPublishCallback,
    unregisterPublishCallback,
    registerDiscardCallback,
    unregisterDiscardCallback,
  } = useEditContext()

  // Track dirty state in EditContext
  useEffect(() => {
    if (isDirty) {
      markDirty(sectionKey)
    } else {
      markClean(sectionKey)
    }
  }, [isDirty, sectionKey, markDirty, markClean])

  // Register publish callback for this section
  const publish = useCallback(async () => {
    await contentPublish()
    markClean(sectionKey)
  }, [contentPublish, markClean, sectionKey])

  useEffect(() => {
    registerPublishCallback(sectionKey, publish)
    return () => unregisterPublishCallback(sectionKey)
  }, [sectionKey, publish, registerPublishCallback, unregisterPublishCallback])

  // Register discard callback
  const discard = useCallback(() => {
    discardChanges()
    markClean(sectionKey)
  }, [discardChanges, markClean, sectionKey])

  useEffect(() => {
    registerDiscardCallback(sectionKey, discard)
    return () => unregisterDiscardCallback(sectionKey)
  }, [sectionKey, discard, registerDiscardCallback, unregisterDiscardCallback])

  return {
    content: content || {},
    isLoading,
    error,
    isDirty,
    updateField,
    publish,
    discard,
  }
}
